// Boards kept only in this browser, for people without an account (spec
// §14.4). IndexedDB holds a small index (for listing) and each board's
// objects; a BroadcastChannel tells other tabs about saves, and a Web Lock
// makes sure only one tab edits a board at a time.
import type { BoardObject } from '../../shared/types.ts';
import { validateObject } from '../../shared/validate.ts';

export interface LocalBoardInfo { id: string; title: string; updatedAt: number; count: number }
export interface LocalBoard extends LocalBoardInfo { objects: BoardObject[] }

const DB_NAME = 'atomic-lab-local';
const INDEX = 'boards';
const CONTENT = 'content';
const CHANNEL = 'atomic-lab-local';
const LOCK_PREFIX = 'atomic-lab-local:';

let dbPromise: Promise<IDBDatabase> | null = null;

function db(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(INDEX, { keyPath: 'id' });
      req.result.createObjectStore(CONTENT, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("This browser can't store boards."));
  });
  return dbPromise;
}

// A page kept in the back/forward cache mustn't hold the database: other pages would wait on it.
addEventListener('pagehide', (e) => {
  if (!e.persisted || !dbPromise) return;
  const closing = dbPromise;
  dbPromise = null;
  void closing.then((d) => d.close(), () => {});
});

const done = (req: IDBRequest | IDBTransaction) => new Promise<void>((resolve, reject) => {
  if (req instanceof IDBTransaction) {
    req.oncomplete = () => resolve();
    req.onerror = req.onabort = () => reject(req.error ?? new Error('Saving failed.'));
  } else {
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('Reading failed.'));
  }
});

function read<T>(store: string, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return db().then((d) => {
    const req = fn(d.transaction(store, 'readonly').objectStore(store));
    return done(req).then(() => req.result);
  });
}

/**
 * Local boards for a page that's about to draw: waits up to `ms` for them, so
 * they normally arrive with the page instead of shifting it as you go to
 * click; null if the browser is slower than that (the page draws without
 * them and adds them when `later` gets them).
 */
export async function localBoardsSoon(ms: number, later: (boards: LocalBoardInfo[]) => void): Promise<LocalBoardInfo[] | null> {
  const list = listLocalBoards();
  const timeout = new Promise<null>((r) => setTimeout(() => r(null), ms));
  const first = await Promise.race([list.catch(() => [] as LocalBoardInfo[]), timeout]);
  if (first === null) void list.then(later, () => {});
  return first;
}

/** Local boards, most recently edited first. */
export async function listLocalBoards(): Promise<LocalBoardInfo[]> {
  const all = await read<LocalBoardInfo[]>(INDEX, (s) => s.getAll());
  return all.sort((a, b) => b.updatedAt - a.updatedAt);
}

/** A local board, with anything invalid (a damaged or tampered record) left out. */
export async function getLocalBoard(id: string): Promise<LocalBoard | null> {
  const [info, content] = await Promise.all([
    read<LocalBoardInfo | undefined>(INDEX, (s) => s.get(id)),
    read<{ id: string; objects: unknown[] } | undefined>(CONTENT, (s) => s.get(id)),
  ]);
  if (!info) return null;
  const objects = (content?.objects ?? []).filter((o) => validateObject(o) === null) as BoardObject[];
  return { ...info, objects };
}

export async function saveLocalBoard(board: LocalBoard): Promise<void> {
  const d = await db();
  const t = d.transaction([INDEX, CONTENT], 'readwrite');
  const { objects, ...info } = board;
  t.objectStore(INDEX).put({ ...info, count: objects.length });
  t.objectStore(CONTENT).put({ id: board.id, objects });
  await done(t);
  announce({ type: 'saved', id: board.id });
}

export async function deleteLocalBoard(id: string): Promise<void> {
  const d = await db();
  const t = d.transaction([INDEX, CONTENT], 'readwrite');
  t.objectStore(INDEX).delete(id);
  t.objectStore(CONTENT).delete(id);
  await done(t);
  announce({ type: 'deleted', id });
}

// --- tabs ---

export type LocalBoardEvent = { type: 'saved' | 'deleted'; id: string };

let channel: BroadcastChannel | null = null;
const bus = () => (channel ??= typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL) : null);

function announce(e: LocalBoardEvent): void {
  bus()?.postMessage(e);
}

/** Saves and deletions made in other tabs. */
export function onLocalBoardChange(fn: (e: LocalBoardEvent) => void): void {
  bus()?.addEventListener('message', (m) => fn(m.data as LocalBoardEvent));
}

/**
 * Claims a board for editing in this tab until `release()` (or the page goes
 * away). `editing` is false if another tab already has it; where Web Locks
 * don't exist, every tab edits.
 */
export function claimLocalBoard(id: string): Promise<{ editing: boolean; release: () => void }> {
  if (!navigator.locks) return Promise.resolve({ editing: true, release: () => {} });
  return new Promise((resolve) => {
    void navigator.locks.request(LOCK_PREFIX + id, { ifAvailable: true }, (lock) => {
      if (!lock) {
        resolve({ editing: false, release: () => {} });
        return undefined;
      }
      return new Promise<void>((release) => resolve({ editing: true, release }));
    });
  });
}

/** Ids of local boards open for editing in some tab. */
export async function claimedLocalBoards(): Promise<Set<string>> {
  if (!navigator.locks) return new Set();
  const { held = [] } = await navigator.locks.query();
  return new Set(held.flatMap((l) => (l.name?.startsWith(LOCK_PREFIX) ? [l.name.slice(LOCK_PREFIX.length)] : [])));
}

/** Asks the browser not to clear local boards when it's short of space. */
export function requestPersistence(): void {
  void navigator.storage?.persist?.().catch(() => {});
}
